import React, { useEffect, useRef, useState } from "react";

import { useMutation } from "graphql-hooks";
import { useRouter } from "next/router";
import styled from "styled-components";

import { Mutations } from "@api/graphql/queries/Mutations";
import { cartAddOfferProduct, cartAddOfferProductVariables, CartType, Offer_offerProducts } from "@api/graphql/types";
import { useLoctool } from "@bigfish/react-loctool";
import { Badge } from "@components/Badge";
import { Button } from "@components/Button";
import { Card } from "@components/Card/Card";
import { OriginalPrice, Price, Saving } from "@components/Card/Elements";
import { DigitalImageFrame } from "@components/DigitalImageFrame";
import { Flex } from "@components/Flex";
import { SVGArrowLeft } from "@components/SVGCollection";
import { Typography } from "@components/Typography";
import { useCart } from "@hooks/useCart";
import { bp } from "@styles/theme";
import { CURRENCY_FORMAT_OPTIONS } from "@utils/Constants";
import { ImageUtils } from "@utils/ImageUtils";
import { Path } from "@utils/Path";
import { HtmlUtils } from "@utils/HtmlUtils";

interface OfferAccordionCardProps {
    offerProduct: Offer_offerProducts;
    open?: boolean;
    group?: string;
    className?: string;
    onImageClick?: () => void;
}

export const OfferAccordionCard = ({ offerProduct, open, group, className, onImageClick }: OfferAccordionCardProps) => {
    const Intl = useLoctool();
    const product = offerProduct.product;
    const router = useRouter();
    const [accordionHeight, setAccordionHeight] = useState<number | null>(null);
    const [addOfferProductMutation, { loading: addOfferProductLoading }] = useMutation<cartAddOfferProduct, cartAddOfferProductVariables>(
        Mutations.cartAddOfferProduct,
    );
    const cart = useCart();
    const cardRef = useRef<HTMLDivElement | null>(null);
    const accordionContentRef = useRef<HTMLDivElement | null>(null);

    const addToCart = async (offer_product_id: number) => {
        await cart.cartFlush(CartType.CART_TYPE_NORMAL);
        await cart.cartFlush(CartType.CART_TYPE_RENEW);
        const result = await addOfferProductMutation({ variables: { offer_product_id } });
        const carts = result.data?.cartAddOfferProduct;
        if (carts) {
            router.push(Path.offerCheckoutSubmit);
        }
    };

    useEffect(() => {
        if (open) {
            cardRef?.current?.classList.add("isOpen");
        }

        setAccordionHeight(accordionContentRef?.current?.scrollHeight ?? null);
    });

    const toggleAccordion = () => {
        const cardClassList = cardRef?.current?.classList;
        const openAccordions = document.querySelectorAll(`.${group}.isOpen`);

        if (cardClassList?.contains("isOpen")) {
            cardClassList.remove("isOpen");
        } else if (group && cardClassList?.contains(group) && !cardClassList.contains("isOpen") && openAccordions !== null) {
            cardClassList.add("isOpen");

            openAccordions.forEach(openAccordion => {
                openAccordion.classList.remove("isOpen");
            });
        } else {
            cardClassList?.add("isOpen");
        }

        accordionContentRef?.current?.addEventListener("transitionstart", () => {
            cardClassList?.add("opening");
        });

        accordionContentRef?.current?.addEventListener("transitionend", () => {
            cardClassList?.remove("opening");
        });
    };

    const discountPercent = offerProduct.discount_percent;

    return (
        <StyledAccordionCard
            onClick={toggleAccordion}
            ref={cardRef}
            $scrollHeight={accordionHeight}
            className={`accordion ${group} ${className}`}
            data-ce-measure-widget={HtmlUtils.removeTags(product.name)}
        >
            <AccordionCardImage
                onClick={e => {
                    if (onImageClick) {
                        e.stopPropagation();
                        onImageClick();
                    }
                }}
            >
                {product.__typename === "ProductDigital" ? (
                    <ProductDigitalImage
                        image={ImageUtils.publicationImageWithFallback(offerProduct.publication, { width: 200 })}
                        alternate={HtmlUtils.removeTags(product.name)}
                    />
                ) : (
                    <ProductPrintImage
                        {...ImageUtils.publicationImageWithFallback(offerProduct.publication, { width: 200 })}
                        alt={HtmlUtils.removeTags(product.name)}
                    />
                )}
                {discountPercent > 0 && <AccordionCardBadge text={`-${discountPercent}%`} color="red" />}
            </AccordionCardImage>
            <AccordionCardHeader>
                <div>
                    {product.text_featured && <UpTitle>{product.text_featured}</UpTitle>}
                    <Title>
                        <div dangerouslySetInnerHTML={{ __html: HtmlUtils.sanitize(product.name) }} />
                    </Title>
                </div>
                <AccordionCardToggle>
                    <SVGArrowLeft />
                </AccordionCardToggle>
            </AccordionCardHeader>
            <div>{product.text_gift && <Gift>{product.text_gift}</Gift>}</div>
            <AccordionCardContent ref={accordionContentRef}>
                {product.text_more && (
                    <SubscriptionDetails>
                        {product.text_more}
                        {product.external_url && (
                            <a
                                onClick={(event: React.MouseEvent) => {
                                    event.stopPropagation();
                                }}
                                href={product.external_url}
                            >
                                {Intl.formatMessage({ id: "common.learnMore" })}
                            </a>
                        )}
                    </SubscriptionDetails>
                )}
            </AccordionCardContent>
            <AccordionCardAction>
                {offerProduct.original_price !== 0 && (
                    <AccordionCardOriginalPrice>{Intl.formatNumber(offerProduct.original_price, CURRENCY_FORMAT_OPTIONS)}</AccordionCardOriginalPrice>
                )}

                <AccordionCardPrice>{Intl.formatNumber(offerProduct.price, CURRENCY_FORMAT_OPTIONS)}</AccordionCardPrice>
                {discountPercent >= 5 && (
                    <AccordionCardSaving>{Intl.formatMessage({ id: "pages.home.wideProductCard.saving" }, { saving: discountPercent })}</AccordionCardSaving>
                )}
                <AccordionCardActionButton
                    disabled={addOfferProductLoading}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        addToCart(offerProduct.id);
                    }}
                >
                    {Intl.formatMessage({ id: "pages.home.wideProductCard.addToOfferCart" })}
                </AccordionCardActionButton>
            </AccordionCardAction>
        </StyledAccordionCard>
    );
};

const AccordionCardImage = styled.div`
    grid-area: image;
    position: relative;
`;

const ProductPrintImage = styled.img`
    width: 100px;
    height: auto;
    margin-right: 26px;
    object-fit: contain;
    object-position: top left;
    transition: all 300ms;
`;

const ProductDigitalImage = styled(DigitalImageFrame)`
    width: 100px;
    height: auto;
    margin-right: 26px;
    transition: all 300ms;
`;

const AccordionCardBadge = styled(Badge)`
    position: absolute;
    bottom: 0;
    left: -25px;
    z-index: 2;
`;

const AccordionCardHeader = styled.div`
    grid-area: header;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
`;

const UpTitle = styled(Typography.UpTitle)`
    margin-bottom: 4px;
    font-size: 20px;
    line-height: 1;
    margin-bottom: 10px;
`;

const Title = styled(Typography.H3)`
    margin-bottom: 0;
    font-size: 17px;
    line-height: 22px;
`;

const AccordionCardContent = styled.div`
    grid-area: hideContent;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 300ms, opacity 300ms;
`;

const AccordionCardAction = styled.div`
    grid-area: action;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
        "originalPrice price"
        "button button"
        "saving saving";
    align-items: baseline;
    justify-items: end;
    width: max-content;
    margin-left: auto;
`;

const AccordionCardOriginalPrice = styled(OriginalPrice)`
    grid-area: originalPrice;
`;

const AccordionCardPrice = styled(Price)`
    grid-area: price;
`;

const AccordionCardSaving = styled(Saving)`
    grid-area: saving;
`;

const AccordionCardActionButton = styled(Button.Primary)<{ onClick?: void }>`
    grid-area: button;
    margin: 8px 0;
`;

const AccordionCardToggle = styled.button`
    color: ${props => props.theme.color.red};

    svg {
        width: 12px;
        transition: transform 300ms;
        transform: rotate(-90deg);
    }
`;

const StyledAccordionCard = styled(Card)<{ $isOpen?: boolean; $scrollHeight: number | null; $openDefault?: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
        "image header"
        "image content"
        "hideContent hideContent"
        "action action";
    align-items: flex-start;
    position: relative;
    width: 100%;
    cursor: pointer;

    ${bp.medium} {
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto 90px auto auto;
        grid-template-areas:
            "image header header"
            "image content action"
            ". hideContent hideContent";
    }

    &.isOpen {
        ${AccordionCardContent} {
            max-height: ${props => props.$scrollHeight}px;
            opacity: 1;
            transition: max-height 300ms, opacity 300ms;
        }

        ${AccordionCardToggle} {
            svg {
                transform: rotate(90deg);
            }
        }
    }
`;

const SubscriptionDetails = styled.div`
    margin-top: 10px;

    a {
        display: block;
        font-weight: 700;
        text-decoration: underline;
    }
`;

const AccordionCardDescription = styled.div`
    grid-area: content;
    max-width: 260px;
`;

export const Gift = styled(Flex.Container).attrs({
    $alignItems: "center",
})`
    width: max-content;
    max-width: 100%;
    padding: 5px 20px;
    margin: 15px 0 10px 0;
    background-color: ${props => props.theme.color.beigeXL};
    text-align: center;
`;
